import React, { useState, useContext, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import SecurityPage from './SecurityPage';
import MentorSharePage from './MentorSharePage';
import SessionCatalog from '../../components/SessionCatalog';
import { useAuth } from '../../context/AuthContext';
import { AppContext } from '../../App';
import { MENTOR_DASHBOARD, MENTOR_REVIEWS, convertPrice, getAvatarUrl } from '../../data/mockData';
import { bookingService, walletService, apiClient, eventService } from '../../services';
import DashboardSkeleton from '../../components/DashboardSkeleton';
import { getCached, setCached } from '../../utils/dashboardCache';
import { MeetingJoinInline } from '../../components/MeetingJoinButton';
import ReviewModal from '../../components/ReviewModal';
import { useIntegrations } from '../../context/IntegrationsContext';
import MentorApplicationStatus from '../../components/MentorApplicationStatus';
import MentorOnboardingTour from '../../components/MentorOnboardingTour';
import MentorSmartBanner from '../../components/MentorSmartBanner';
import { hasMentorSeenTour, markMentorTourSeen, setMentorAppStatus } from '../../utils/mentorApp';
import ProfilePhotoUpload from '../../components/ProfilePhotoUpload';

const D = MENTOR_DASHBOARD;

const EVENT_TYPES = ['workshop','webinar','qa','office_hours','hackathon'];
const EMPTY_EVENT = {
  title:'', type:'workshop', description:'', date:'', time:'',
  duration:'60', priceType:'free', priceINR:'', maxAttendees:'100',
  meetingPlatform:'google_meet', meetingLink:'',
  linkedin:'', twitter:'',
};

function Av({ seed, bg, init, size = 38 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: 10, overflow: 'hidden', flexShrink: 0, background: `#${bg}`, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <img src={getAvatarUrl(seed, bg, size)} alt={init} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display='none'} />
      <span style={{ position: 'absolute', fontWeight: 800, fontSize: '.72rem', color: '#fff' }}>{init}</span>
    </div>
  );
}

function Overview({ upcoming: _up, wallet: _wallet, stats: _stats, user: _user }) {
  const isMock   = process.env.REACT_APP_MOCK !== 'false';
  const upcoming = _up     || (isMock ? D.upcomingSessions : []);
  const wallet   = _wallet || { balance: isMock ? D.stats.walletBalance : 0, totalEarned: isMock ? D.stats.totalEarnings : 0 };
  const stats    = _stats  || (isMock ? D.stats : {});
  const [avail, setAvail] = useState(isMock ? D.stats.isAvailable : false);
  const { currency } = useContext(AppContext);
  const nav = useNavigate();
  return (
    <div>
      {/* ── Smart banner: next session + profile checklist ── */}
      <MentorSmartBanner user={_user} upcoming={upcoming} />

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:24 }}>
        <div><h1 className="db-page-title">Welcome back, {_user?.firstName || _user?.name?.split(' ')[0] || 'Mentor'} 👋</h1><p className="db-page-sub" style={{marginBottom:0}}>Here's your mentoring overview for today.</p></div>
        <div className="db-toggle-wrap">
          <button className={`db-toggle ${avail?'on':''}`} onClick={()=>setAvail(!avail)} />
          <span className="db-toggle-label" style={{color:avail?'#16a34a':'#6b7280'}}>{avail?'● Available':'○ Not Available'}</span>
        </div>
      </div>
      <div className="db-stats-grid-4" style={{marginBottom:24}}>
        {[{icon:'📅',label:'Total Sessions',value:stats.totalSessions||0,sub:`${stats.upcoming||0} upcoming`,trend:'+12%',up:true},{icon:'✅',label:'Completed',value:stats.completed||0,sub:`${stats.cancelled||0} cancelled`,trend:'+8%',up:true},{icon:'💰',label:'This Month',value:convertPrice(stats.monthEarnings||0,currency),sub:'Earnings',trend:'+18%',up:true},{icon:'⭐',label:'Avg Rating',value:stats.avgRating||0,sub:`${stats.totalReviews||0} reviews`,trend:'+0.02',up:true}].map(s=>(
          <div key={s.label} className="db-stat-card"><div className="dsc-icon">{s.icon}</div><div className="dsc-label">{s.label}</div><div className="dsc-value">{s.value}</div><div style={{display:'flex',alignItems:'center',gap:6}}><span className="dsc-sub">{s.sub}</span><span className={`dsc-trend ${s.up?'up':'down'}`}>{s.up?'↑':'↓'} {s.trend}</span></div></div>
        ))}
      </div>
      <div className="db-grid-2" style={{marginBottom:24}}>
        <div className="db-card">
          <div className="db-section-header"><span className="db-section-title">📅 Upcoming Sessions</span><button className="db-section-action" onClick={()=>nav('/dashboard/mentor/sessions')}>View all →</button></div>
          {upcoming.slice(0,3).map(s=>(
            <div key={s.id} className="session-row">
              <Av seed={s.menteeAvatar} bg={s.menteeBg} init={s.menteeInit} />
              <div className="sr-info"><div className="sr-name">{s.mentee}</div><div className="sr-meta">{s.type} · {s.date} {s.time}</div><div style={{fontSize:'.7rem',color:'#9ca3af'}}>{s.topic}</div></div>
              <div className="sr-right"><span className={`st-badge st-${s.status}`}>{s.status}</span><div className="sr-date">{convertPrice(s.priceINR,currency)}</div></div>
            </div>
          ))}
        </div>
        <div className="db-card">
          <div className="db-section-header"><span className="db-section-title">📈 Earnings (6 Months)</span><button className="db-section-action" onClick={()=>nav('/dashboard/mentor/earnings')}>Details →</button></div>
          <div className="db-bar-chart">
            {(isMock ? D.monthlyEarnings : []).map(m=>{const arr=(isMock ? D.monthlyEarnings : []);const max=Math.max(...arr.map(x=>x.amount));return(<div key={m.month} className="db-bar-col"><div className="db-bar-val">{(m.amount/1000).toFixed(0)}K</div><div className="db-bar" style={{height:`${max>0?(m.amount/max)*100:0}%`}}/><div className="db-bar-lbl">{m.month}</div></div>);})}
          </div>
          <div style={{display:'flex',gap:20,marginTop:14,paddingTop:12,borderTop:'1px solid #f3f4f6'}}>
            {[{l:'Wallet',v:wallet.balance||0,c:'#1a1a2e'},{l:'Pending',v:stats.pendingPayout||0,c:'#b45309'},{l:'Total Earned',v:wallet.totalEarned||0,c:'#16a34a'}].map(x=>(
              <div key={x.l}><div style={{fontSize:'.62rem',fontWeight:800,color:'#9ca3af',textTransform:'uppercase'}}>{x.l}</div><div style={{fontSize:'.95rem',fontWeight:800,color:x.c}}>{convertPrice(x.v,currency)}</div></div>
            ))}
          </div>
        </div>
      </div>
      <div className="db-grid-2">
        <div className="db-card">
          <div className="db-section-title" style={{marginBottom:14}}>🏦 Wallet</div>
          <div style={{background:'#f9fafb',borderRadius:12,padding:16,marginBottom:14}}><div style={{fontSize:'.65rem',fontWeight:800,color:'#9ca3af',textTransform:'uppercase'}}>Available Balance</div><div style={{fontSize:'2rem',fontWeight:800,color:'#1a1a2e',marginTop:4}}>{convertPrice(wallet.balance||0,currency)}</div></div>
          <div style={{display:'flex',gap:8}}><button className="btn btn-primary btn-sm" style={{flex:1,justifyContent:'center'}}>Request Payout</button><button className="btn btn-outline btn-sm" style={{flex:1,justifyContent:'center'}}>History</button></div>
          <div style={{marginTop:12,padding:'10px 12px',background:'#fffbeb',borderRadius:8,fontSize:'.75rem',color:'#b45309',fontWeight:600}}>⏳ Pending: {convertPrice(stats.pendingPayout||0,currency)} — 2 business days</div>
        </div>
        <div className="db-card">
          <div className="db-section-title" style={{marginBottom:14}}>📊 Performance</div>
          {[{l:'Profile Views',v:'1,248',t:'+12%',up:true},{l:'Booking Rate',v:'68%',t:'+5%',up:true},{l:'Repeat Bookings',v:'41%',t:'+3%',up:true},{l:'Response Time',v:'< 1 hr',t:'Excellent',up:true}].map(p=>(
            <div key={p.l} style={{display:'flex',justifyContent:'space-between',padding:'9px 0',borderBottom:'1px solid #f3f4f6',alignItems:'center'}}>
              <span style={{fontSize:'.84rem',color:'#6b7280',fontWeight:600}}>{p.l}</span>
              <div style={{display:'flex',gap:8,alignItems:'center'}}><span style={{fontWeight:800,color:'#1a1a2e'}}>{p.v}</span><span className="dsc-trend up">{p.t}</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Sessions({ upcoming: _up, past: _past }) {
  const isMock   = process.env.REACT_APP_MOCK !== 'false';
  const upcoming = _up || (isMock ? D.upcomingSessions : []);
  const past     = _past || (isMock ? (D.pastSessions || []) : []);
  const [tab, setTab] = useState('upcoming');
  const { currency } = useContext(AppContext);
  const [notes, setNotes] = useState({});
  const [upcomingList, setUpcomingList] = useState(upcoming);
  const [reviewModal, setReviewModal] = useState(null);
  const [, setReviewedIds] = useState([]);
  const completed = past.filter(s=>s.status==='completed');
  const cancelled = past.filter(s=>s.status==='cancelled');

  const handleMarkComplete = (s) => {
    setUpcomingList(prev => prev.filter(x => x.id !== s.id));
    // Mentor sees a prompt asking them to request a review from the mentee
    setTimeout(() => setReviewModal({
      ...s,
      mentor: s.mentee,        // from mentor's view, "mentor" field = the person being reviewed
      mentorAvatar: s.menteeAvatar || s.mentee,
      mentorBg: s.menteeBg,
      mentorInit: s.menteeInit,
      type: s.type, date: s.date, duration: s.duration,
      isMentorView: true,
    }), 400);
  };

  return (
    <div>
      <h1 className="db-page-title">Sessions</h1><p className="db-page-sub">Manage your mentoring sessions.</p>
      <div className="db-tabs">{[['upcoming',`Upcoming (${upcomingList.length})`],['past',`Completed (${completed.length})`],['cancelled',`Cancelled (${cancelled.length})`]].map(([k,l])=>(<button key={k} className={`db-tab ${tab===k?'active':''}`} onClick={()=>setTab(k)}>{l}</button>))}</div>
      {tab==='upcoming' && <div style={{display:'flex',flexDirection:'column',gap:14}}>
        {upcomingList.map(s=>(
          <div key={s.id} className="db-card">
            <div style={{display:'flex',gap:14,alignItems:'flex-start',flexWrap:'wrap'}}>
              <Av seed={s.menteeAvatar} bg={s.menteeBg} init={s.menteeInit} size={46} />
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:4}}><span style={{fontWeight:800,fontSize:'.9375rem',color:'#1a1a2e'}}>{s.mentee}</span><span className={`st-badge st-${s.status}`}>{s.status}</span></div>
                <div style={{fontSize:'.82rem',color:'#6b7280',marginBottom:3}}>{s.type} · {s.duration}</div>
                <div style={{fontSize:'.84rem',fontWeight:600,color:'#374151'}}>📌 {s.topic}</div>
                {(s.menteeName || s.mentee) && (
                  <div style={{ fontSize:'.75rem', color:'#9ca3af', marginTop:3 }}>
                    👤 {s.menteeName || s.mentee}{s.menteeCompany ? ` · ${s.menteeCompany}` : ''}{s.menteeExperience ? ` · ${s.menteeExperience}` : ''}
                  </div>
                )}
                <div style={{fontSize:'.78rem',color:'#9ca3af',marginTop:4}}>📅 {s.date} at {s.time} · {convertPrice(s.priceINR,currency)}</div>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:8,alignItems:'flex-end'}}>
                {<MeetingJoinInline platform={s.meetingPlatform || 'google_meet'} url={s.meetLink} date={s.date} time={s.time} />}
                <button className="btn btn-outline btn-sm">✎ Reschedule</button>
                <button className="btn btn-outline btn-sm" style={{borderColor:'#fca5a5',color:'#dc2626'}}>✕ Cancel</button>
              </div>
            </div>
            <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid #f3f4f6'}}>
              <label className="db-field-label">Session Notes</label>
              <textarea className="db-field db-textarea" style={{minHeight:70}} placeholder="Add prep notes..." value={notes[s.id]||''} onChange={e=>setNotes(n=>({...n,[s.id]:e.target.value}))} />
              <div style={{display:'flex',gap:8,marginTop:8}}>
                <button className="btn btn-primary btn-sm" style={{background:'#16a34a',borderColor:'#16a34a'}} onClick={() => handleMarkComplete(s)}>✅ Mark Complete</button>
                <button className="btn btn-outline btn-sm">💾 Save Notes</button>
              </div>
            </div>
          </div>
        ))}
        {upcomingList.length === 0 && (
          <div style={{textAlign:'center',padding:'48px 20px',color:'#9ca3af'}}>
            <div style={{fontSize:'2.5rem',marginBottom:12}}>🎉</div>
            <div style={{fontWeight:700,color:'#374151',marginBottom:6}}>All sessions completed!</div>
            <div style={{fontSize:'.84rem'}}>Check the Completed tab for session history.</div>
          </div>
        )}
      </div>}
      {tab==='past' && <div className="db-card"><div className="db-table-wrap"><table className="db-table"><thead><tr><th>Mentee</th><th>Type</th><th>Date</th><th>Amount</th><th>Status</th><th>Rating</th><th>Action</th></tr></thead><tbody>
        {completed.map(s=>(<tr key={s.id}><td><div style={{display:'flex',alignItems:'center',gap:8}}><div className="db-mini-av" style={{background:`#${s.menteeBg}`}}>{s.menteeInit}</div>{s.mentee}</div></td><td>{s.type}</td><td>{s.date}</td><td style={{fontWeight:800}}>{convertPrice(s.priceINR,currency)}</td><td><span className="st-badge st-completed">completed</span></td><td>{s.rating?<span style={{color:'#f59e0b'}}>{'★'.repeat(s.rating)}</span>:'—'}</td><td><button style={{fontSize:'.72rem',color:'#16a34a',background:'none',border:'none',cursor:'pointer',fontWeight:700}}>📄 Invoice</button></td></tr>))}
      </tbody></table></div></div>}
      {tab==='cancelled' && <div className="db-card"><div className="db-table-wrap"><table className="db-table"><thead><tr><th>Mentee</th><th>Type</th><th>Date</th><th>Amount</th></tr></thead><tbody>
        {cancelled.map(s=>(<tr key={s.id}><td>{s.mentee}</td><td>{s.type}</td><td>{s.date}</td><td>{convertPrice(s.priceINR,currency)}</td></tr>))}
      </tbody></table></div></div>}

      {/* Review modal — opens when mentor marks session complete */}
      {reviewModal && (
        <ReviewModal
          session={reviewModal}
          onSubmit={() => { setReviewedIds(p => [...p, reviewModal.id]); setReviewModal(null); }}
          onClose={() => setReviewModal(null)}
        />
      )}
    </div>
  );
}

// Convert "2:00 PM" → "14:00", "10:00 AM" → "10:00" (24-hour format for Mongoose schema)
function to24h(t) {
  const [time, period] = t.split(' ');
  let [h, m] = time.split(':').map(Number);
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2,'0')}:${String(m||0).padStart(2,'0')}`;
}
// Expand "09:00"…"17:00" (exclusive) into ["9:00 AM","10:00 AM",…,"4:00 PM"]
// Matches the backend loop: while (h < eh) { push slot; h+=1h; }
function expandTimeSlots(startTime, endTime) {
  if (!startTime || !endTime) return [];
  const slots = [];
  let [h, m] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  while (h < eh || (h === eh && m < em)) {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const disp = h === 0 ? 12 : h > 12 ? h - 12 : h;
    slots.push(`${disp}:${String(m).padStart(2,'0')} ${ampm}`);
    m += 60; if (m >= 60) { h++; m -= 60; }
  }
  return slots;
}
// Sort display-format slots chronologically using their 24h value
function sortSlots(slots) {
  return [...slots].sort((a,b)=>{
    const [ah,am]=to24h(a).split(':').map(Number);
    const [bh,bm]=to24h(b).split(':').map(Number);
    return (ah*60+am)-(bh*60+bm);
  });
}

function Availability() {
  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const isMockA = process.env.REACT_APP_MOCK !== 'false';
  const { user: _avUser } = useAuth();
  const [days,      setDays]       = useState(isMockA ? D.slots.availableDays : ['Mon','Tue','Wed','Thu','Fri']);
  const [activeSlots,setActiveSlots]= useState(isMockA ? D.slots.timeSlots : ['10:00 AM','11:00 AM','2:00 PM','3:00 PM']);
  const [maxPerDay, setMaxPerDay]  = useState(isMockA ? D.slots.maxPerDay : 4);
  const [buffer,    setBuffer]     = useState(isMockA ? D.slots.bufferMinutes : 15);
  const [sameDay,       setSameDay]       = useState(isMockA ? D.slots.sameDayBooking : false);
  const [minDaysAdvance, setMinDaysAdvance] = useState(1); // only used when sameDay is false
  const [minHoursBefore, setMinHoursBefore] = useState(isMockA ? 3 : 3);
  const [saved,     setSaved]      = useState(false);
  const [saving,    setSaving]     = useState(false);
  const [saveError, setSaveError]  = useState('');

  // Load real availability from API
  useEffect(()=>{
    if(isMock) return;
    if(!sessionStorage.getItem('db_user')) return;
    apiClient.get('/mentors/me/profile').then(res=>{
      const m=res.mentor||res;
      if(m.availabilitySlots?.length){
        const dayNames=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
        // Restore available days
        const loadedDays=[...new Set(m.availabilitySlots.map(s=>dayNames[s.day]))];
        setDays(loadedDays);
        // Restore time slots — expand the stored startTime..endTime window back into
        // individual slot chips (same logic the booking API uses to generate slots)
        const first=m.availabilitySlots[0];
        if(first?.startTime && first?.endTime){
          const expanded=expandTimeSlots(first.startTime, first.endTime);
          if(expanded.length) setActiveSlots(expanded);
        }
      }
      if(m.maxSessionsPerDay != null) setMaxPerDay(m.maxSessionsPerDay);
      if(m.bufferMinutes     != null) setBuffer(m.bufferMinutes);
      if(m.sameDayBooking    != null) setSameDay(m.sameDayBooking);
      if(m.minHoursBeforeBooking != null) setMinHoursBefore(m.minHoursBeforeBooking);
      if(m.minDaysBeforeBooking  != null) setMinDaysAdvance(m.minDaysBeforeBooking);
    }).catch(()=>{});
  },[]);// eslint-disable-line
  const allDays=['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const allSlots=['8:00 AM','9:00 AM','10:00 AM','11:00 AM','12:00 PM','1:00 PM','2:00 PM','3:00 PM','4:00 PM','5:00 PM','6:00 PM','7:00 PM','8:00 PM','9:00 PM'];

  // Detect stale mock token even when REACT_APP_MOCK=false
  const hasMockToken = !sessionStorage.getItem('db_user');

  const save=async()=>{
    setSaving(true);
    setSaveError('');
    console.log('[Availability] isMock=', isMock, 'hasMockToken=', hasMockToken);

    // Guard: env says real mode but still using a mock token → force re-login
    if(!isMock && hasMockToken){
      setSaveError('Session mismatch — you are using a mock login token. Please log out and log in again to save to MongoDB.');
      setSaving(false);
      return;
    }

    // Guard: running in mock mode (REACT_APP_MOCK was true when server started)
    if(isMock){
      setSaveError('App is running in mock mode — data is NOT saved to MongoDB. Stop the dev server, confirm REACT_APP_MOCK=false in .env, then run "npm start" again.');
      setSaving(false);
      return;
    }

    try{
      const dayNames=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      // Sort slots chronologically before picking start/end times
      const sorted = sortSlots(activeSlots);
      const startTime = sorted.length ? to24h(sorted[0]) : '09:00';
      // endTime is EXCLUSIVE in the backend loop, so add 1 hour to the last slot
      // so that the last selected slot is included when generating availability
      const lastSlot24 = sorted.length ? to24h(sorted[sorted.length-1]) : '17:00';
      const lastH = parseInt(lastSlot24.split(':')[0], 10);
      const lastM = lastSlot24.split(':')[1];
      const endTime = `${String(lastH + 1).padStart(2,'0')}:${lastM}`;
      const slots=days.map(d=>({
        day: dayNames.indexOf(d),
        startTime,
        endTime,
        timezone:'IST',
      }));
      console.log('[Availability] PUT /mentors/me/availability', slots);
      await apiClient.put('/mentors/me/availability',{
        slots,
        maxSessionsPerDay:    maxPerDay,
        bufferMinutes:        buffer,
        sameDayBooking:        sameDay,
        minDaysBeforeBooking:  sameDay ? 0 : minDaysAdvance,
        minHoursBeforeBooking: minHoursBefore,
      });
      setSaved(true); setTimeout(()=>setSaved(false),2500);
      // Mark availability as saved so the profile checklist detects it
      if (_avUser?.id) localStorage.setItem(`db_mentor_avail_saved_${_avUser.id}`, '1');
    }catch(e){
      console.error('Availability save error',e);
      setSaveError(e?.message || 'Failed to save availability. Please try again.');
    }
    finally{ setSaving(false); }
  };
  return (
    <div>
      <h1 className="db-page-title">Availability & Slots</h1><p className="db-page-sub">Configure when mentees can book you.</p>
      <div className="db-grid-2">
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div className="db-card"><div className="db-section-title" style={{marginBottom:14}}>📆 Available Days</div><div className="day-chips">{allDays.map(d=>(<button key={d} className={`day-chip ${days.includes(d)?'on':''}`} onClick={()=>setDays(p=>p.includes(d)?p.filter(x=>x!==d):[...p,d])}>{d}</button>))}</div></div>
          <div className="db-card">
            <div className="db-section-title" style={{marginBottom:14}}>⚙️ Settings</div>
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              <div><label className="db-field-label">Max Sessions/Day</label><select className="db-field" value={maxPerDay} onChange={e=>setMaxPerDay(+e.target.value)}>{[1,2,3,4,5,6].map(n=><option key={n} value={n}>{n} sessions</option>)}</select></div>
              <div><label className="db-field-label">Buffer Between Sessions</label><select className="db-field" value={buffer} onChange={e=>setBuffer(+e.target.value)}>{[0,10,15,20,30,45,60].map(n=><option key={n} value={n}>{n} min</option>)}</select></div>
              <div>
                <div className="db-toggle-wrap" style={{marginBottom: sameDay ? 0 : 10}}>
                  <button className={`db-toggle ${sameDay?'on':''}`} onClick={()=>setSameDay(!sameDay)}/>
                  <span className="db-toggle-label">Allow same-day booking</span>
                </div>
                {!sameDay && (
                  <div style={{paddingLeft:52}}>
                    <label className="db-field-label" style={{marginBottom:6,display:'block'}}>Minimum days in advance</label>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                      {[1,2,3,4].map(n=>(
                        <button
                          key={n}
                          onClick={()=>setMinDaysAdvance(n)}
                          style={{
                            padding:'7px 18px', borderRadius:20, border:'1.5px solid',
                            borderColor: minDaysAdvance===n ? 'var(--green)' : 'var(--line)',
                            background:  minDaysAdvance===n ? 'var(--green-pale)' : '#fff',
                            color:       minDaysAdvance===n ? 'var(--green-d)' : 'var(--ink3)',
                            fontWeight:700, fontSize:'.8rem', cursor:'pointer', transition:'all .15s',
                          }}
                        >
                          {n} {n===1?'day':'days'}
                        </button>
                      ))}
                    </div>
                    <div style={{fontSize:'.72rem',color:'var(--ink4)',marginTop:6}}>
                      Mentees must book at least {minDaysAdvance} {minDaysAdvance===1?'day':'days'} before the session.
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="db-field-label">Min notice before booking</label>
                <select className="db-field" value={minHoursBefore} onChange={e=>setMinHoursBefore(+e.target.value)}>
                  {[1,2,3,4,6,8,12,24,48].map(n=><option key={n} value={n}>{n} {n===1?'hour':'hours'} before</option>)}
                </select>
                <div style={{fontSize:'.72rem',color:'var(--ink4)',marginTop:4}}>Mentees must book at least this many hours before the session starts.</div>
              </div>
            </div>
          </div>
        </div>
        <div className="db-card">
          <div className="db-section-title" style={{marginBottom:14}}>🕐 Time Slots</div>
          <div style={{display:'flex',flexWrap:'wrap',gap:8}}>{allSlots.map(s=>(<button key={s} className={`day-chip ${activeSlots.includes(s)?'on':''}`} onClick={()=>setActiveSlots(p=>p.includes(s)?p.filter(x=>x!==s):[...p,s])} style={{fontSize:'.75rem'}}>{s}</button>))}</div>
          <div style={{marginTop:14,padding:'10px 14px',background:'#f0fdf4',borderRadius:10,fontSize:'.78rem',color:'#16a34a',fontWeight:600}}>✓ {activeSlots.length} slots on {days.length} days = {activeSlots.length*days.length} possible slots/week</div>
        </div>
      </div>
      {saveError && (
        <div style={{marginTop:12,padding:'12px 16px',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:10,fontSize:'.82rem',color:'#dc2626',fontWeight:500,lineHeight:1.5}}>
          ⚠️ {saveError}
        </div>
      )}
      <div style={{marginTop:20,display:'flex',gap:12}}><button className="btn btn-primary" style={{minWidth:160}} onClick={save}>{saved?'✓ Saved!':saving?'Saving…':'💾 Save Availability'}</button><button className="btn btn-outline">Reset</button></div>
    </div>
  );
}

function Earnings({ wallet: _walletProp = { balance: 0, totalEarned: 0 } }) {
  const { currency } = useContext(AppContext);
  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const [wallet, setWallet]       = useState(isMock ? { balance: D.stats.walletBalance, totalEarned: D.stats.totalEarnings, totalPaidOut: 0, transactions: D.transactions||[] } : _walletProp);
  const [txns,   setTxns]         = useState(isMock ? (D.transactions||[]) : []);
  const [payAmt, setPayAmt]       = useState('');
  const [payLoading, setPayLoading] = useState(false);
  const [payMsg,   setPayMsg]     = useState('');
  const [walletLoading, setWalletLoading] = useState(!isMock);

  useEffect(()=>{
    if(isMock) return;
    if(!sessionStorage.getItem('db_user')){ setWalletLoading(false); return; }
    walletService.getWallet().then(res=>{
      setWallet(res);
      setTxns((res.transactions||[]).map((t,i)=>({
        id: t._id||i,
        date: t.date ? new Date(t.date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : '-',
        description: t.description||'-',
        gross:      t.type==='credit'  ? t.amountINR : 0,
        commission: t.type==='credit'  ? Math.round(t.amountINR*0.15) : 0,
        net:        t.type==='payout'  ? -t.amountINR : Math.round(t.amountINR*0.85),
        status:     t.type==='payout'  ? 'payout' : 'paid',
      })));
    }).catch(()=>{}).finally(()=>setWalletLoading(false));
  },[]);// eslint-disable-line

  const requestPayout = async () => {
    if(!payAmt||isNaN(payAmt)||+payAmt<500){ setPayMsg('Minimum payout is ₹500'); return; }
    setPayLoading(true); setPayMsg('');
    try{
      if(isMock){ await new Promise(r=>setTimeout(r,800)); setPayMsg('Payout requested! (mock)'); }
      else{ await walletService.requestPayout({ amount: +payAmt }); setPayMsg('✅ Payout requested successfully!'); setPayAmt(''); walletService.getWallet().then(r=>setWallet(r)).catch(()=>{}); }
    }catch(err){ setPayMsg(err.message||'Payout failed'); }
    finally{ setPayLoading(false); }
  };

  return (
    <div>
      <h1 className="db-page-title">Earnings & Payments</h1><p className="db-page-sub">Track income, wallet, and payouts.</p>
      {walletLoading ? <div style={{padding:40,textAlign:'center',color:'#9ca3af'}}>Loading wallet…</div> : (<>
      <div className="db-stats-grid-4" style={{marginBottom:24}}>
        {[{icon:'💰',label:'Total Earnings',v:wallet.totalEarned||0},{icon:'🏦',label:'Wallet Balance',v:wallet.balance||wallet.balanceINR||0},{icon:'📤',label:'Total Paid Out',v:wallet.totalPaidOut||0},{icon:'⏳',label:'Pending',v:wallet.pendingPayout||0}].map(s=>(
          <div key={s.label} className="db-stat-card"><div className="dsc-icon">{s.icon}</div><div className="dsc-label">{s.label}</div><div className="dsc-value" style={{fontSize:'1.3rem'}}>{convertPrice(s.v,currency)}</div></div>
        ))}
      </div>
      <div className="db-grid-2" style={{marginBottom:24}}>
        <div className="db-card">
          <div className="db-section-title" style={{marginBottom:14}}>📊 Transaction Summary</div>
          <div style={{padding:16,background:'#f0fdf4',borderRadius:12,marginBottom:12}}><div style={{fontSize:'.65rem',fontWeight:800,color:'#9ca3af',textTransform:'uppercase'}}>Available for Payout</div><div style={{fontSize:'2rem',fontWeight:800,color:'#16a34a',marginTop:4}}>{convertPrice(wallet.balance||wallet.balanceINR||0,currency)}</div></div>
          <div style={{display:'flex',gap:16,fontSize:'.8rem'}}><div><div style={{fontWeight:800}}>{txns.filter(t=>t.gross>0).length}</div><div style={{color:'#9ca3af'}}>Credits</div></div><div><div style={{fontWeight:800}}>{txns.filter(t=>t.status==='payout').length}</div><div style={{color:'#9ca3af'}}>Payouts</div></div></div>
        </div>
        <div className="db-card">
          <div className="db-section-title" style={{marginBottom:14}}>💳 Request Payout</div>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <div><label className="db-field-label">Amount (₹)</label><input className="db-field" type="number" placeholder="Min ₹500" value={payAmt} onChange={e=>setPayAmt(e.target.value)}/></div>
            <button className="btn btn-primary" style={{justifyContent:'center'}} onClick={requestPayout} disabled={payLoading}>{payLoading?'Processing…':'🏦 Request Payout'}</button>
            {payMsg&&<p style={{fontSize:'.78rem',color:payMsg.startsWith('✅')?'#16a34a':'#dc2626',textAlign:'center',fontWeight:600}}>{payMsg}</p>}
            <p style={{fontSize:'.72rem',color:'#9ca3af',textAlign:'center'}}>15% platform commission already deducted · 2–3 business days</p>
          </div>
        </div>
      </div>
      <div className="db-card">
        <div className="db-section-header"><span className="db-section-title">📋 Transaction History</span></div>
        {txns.length===0 ? <div style={{padding:24,textAlign:'center',color:'#9ca3af'}}>No transactions yet.</div> : (
        <div className="db-table-wrap"><table className="db-table"><thead><tr><th>Date</th><th>Description</th><th>Gross</th><th>Commission</th><th>Net</th><th>Status</th></tr></thead><tbody>
          {txns.map((t,i)=>(<tr key={t.id||i}><td>{t.date}</td><td style={{maxWidth:260,overflow:'hidden',textOverflow:'ellipsis'}}>{t.description}</td><td style={{fontWeight:700}}>{t.gross>0?convertPrice(t.gross,currency):'—'}</td><td style={{color:'#dc2626'}}>{t.commission>0?`-${convertPrice(t.commission,currency)}`:'—'}</td><td style={{fontWeight:800,color:t.net<0?'#dc2626':'#16a34a'}}>{convertPrice(Math.abs(t.net),currency)}</td><td><span className={`st-badge st-${t.status}`}>{t.status}</span></td></tr>))}
        </tbody></table></div>)}
      </div>
      </>)}
    </div>
  );
}

function Profile() {
  const { user, updateUser } = useAuth();
  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const slug = user?.mentorSlug || (user?.email || '').split('@')[0].toLowerCase();

  const [f, setF] = useState({
    name:       user?.name || '',
    headline:   user?.headline || '',
    bio:        user?.bio || '',
    location:   user?.location || '',
    timezone:   user?.timezone || '',
    phone:      user?.phone || '',
    languages:  Array.isArray(user?.languages) ? user.languages.join(', ') : (user?.languages || ''),
    linkedin:   user?.linkedinUrl || '',
    github:     user?.githubUrl || '',
    twitter:    user?.twitter || '',
    portfolio:  user?.portfolio || '',
    company:    user?.company || '',
    role:       user?.headline || '',
    experience: String(user?.experience || ''),
    skills:     Array.isArray(user?.skills) ? user.skills.join(', ') : (user?.skills || ''),
    price:      '1500',
  });
  const [workExp,  setWorkExp]  = useState(user?.workExperience  || []);
  const [educ,     setEduc]     = useState(user?.education       || []);
  const [certs,    setCerts]    = useState(user?.certifications  || []);
  const [saved,  setSaved]  = useState(false);
  const [saving, setSaving] = useState(false);

  // Load saved profile data on mount
  useEffect(() => {
    // 1. Load from localStorage (dashboard-saved rich profile)
    try {
      const raw = localStorage.getItem(`db_mentor_profile_${slug}`);
      if (raw) {
        const stored = JSON.parse(raw);
        setF(p => ({
          ...p,
          name:       stored.name       || p.name,
          headline:   stored.headline   || p.headline,
          bio:        stored.bio        || p.bio,
          location:   stored.location   || p.location,
          timezone:   stored.timezone   || p.timezone,
          phone:      stored.phone      || p.phone,
          languages:  Array.isArray(stored.languages) ? stored.languages.join(', ') : (stored.languages || p.languages),
          linkedin:   stored.linkedinUrl || stored.linkedin || p.linkedin,
          github:     stored.githubUrl   || stored.github   || p.github,
          twitter:    stored.twitter    || p.twitter,
          portfolio:  stored.portfolio  || p.portfolio,
          company:    stored.company    || p.company,
          role:       stored.role       || p.role,
          experience: stored.experience !== undefined ? String(stored.experience) : p.experience,
          skills:     Array.isArray(stored.skills) ? stored.skills.join(', ') : (stored.skills || p.skills),
          price:      stored.price      || p.price,
        }));
        if (stored.workExperience?.length) setWorkExp(stored.workExperience);
        if (stored.education?.length)      setEduc(stored.education);
        if (stored.certifications?.length) setCerts(stored.certifications);
      }
    } catch (_) {}

    // 2. Load from real API (MOCK=false)
    if (!isMock) {
      if (!sessionStorage.getItem('db_user')) return;
      apiClient.get('/mentors/me/profile').then(res => {
        const m = res.mentor || res;
        setF(p => ({
          ...p,
          skills:     (m.skills || []).join(', ') || p.skills,
          company:    m.company    || p.company,
          experience: String(m.experience || p.experience),
          price:      String(m.sessionTypes?.[0]?.priceINR || p.price),
        }));
      }).catch(() => {});
    }
  }, []); // eslint-disable-line

  const addWorkExp = () => setWorkExp(p => [...p, { id: Date.now(), company: '', role: '', startYear: '', endYear: '', isCurrent: false, description: '' }]);
  const addEduc    = () => setEduc(p => [...p, { id: Date.now(), institution: '', degree: '', year: '' }]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        name:             f.name,
        headline:         f.headline,
        bio:              f.bio,
        location:         f.location,
        timezone:         f.timezone,
        phone:            f.phone,
        languages:        f.languages.split(',').map(s => s.trim()).filter(Boolean),
        linkedinUrl:      f.linkedin,
        githubUrl:        f.github,
        twitter:          f.twitter,
        portfolio:        f.portfolio,
        company:          f.company,
        role:             f.role,
        experience:       parseInt(f.experience) || 0,
        skills:           f.skills.split(',').map(s => s.trim()).filter(Boolean),
        price:            f.price,
        workExperience:   workExp,
        education:        educ,
        certifications:   certs,
        profileComplete:  Math.min(100, (user?.profileComplete || 30) + 20),
      };

      if (!isMock) {
        // TODO §BACKEND: PUT /api/users/me { headline, bio, location, linkedinUrl, githubUrl, twitter, portfolio, phone, timezone, languages }
        // const res = await fetch(`${process.env.REACT_APP_API_URL}/users/me`, {
        //   method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        //   body: JSON.stringify(patch),
        // });
        await apiClient.put('/users/me', {
          headline: f.headline, bio: f.bio, location: f.location,
          linkedinUrl: f.linkedin, githubUrl: f.github,
          twitter: f.twitter, portfolio: f.portfolio,
        });
        // TODO §BACKEND: PUT /api/mentors/me/profile { company, experience, skills, workExperience, education, certifications, price }
        // const res2 = await fetch(`${process.env.REACT_APP_API_URL}/mentors/me/profile`, {
        //   method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        //   body: JSON.stringify({ company: f.company, experience: parseInt(f.experience)||0, skills: patch.skills, workExperience: workExp, education: educ, certifications: certs }),
        // });
        await apiClient.put('/mentors/me/profile', {
          company:    f.company,
          experience: parseInt(f.experience) || 0,
          skills:     patch.skills,
          workExperience: workExp,
          education:  educ,
          certifications: certs,
        });
      }

      // TODO §BACKEND: updateUser will call PATCH /api/users/me once backend is wired
      if (updateUser) updateUser(patch);

      // Persist rich profile to localStorage for public profile page to read
      localStorage.setItem(`db_mentor_profile_${slug}`, JSON.stringify({
        ...patch,
        email:     user?.email,
        avatarBg:  user?.avatarBg,
        initials:  user?.initials,
        savedAt:   Date.now(),
      }));

      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('Profile save error', err);
    } finally {
      setSaving(false);
    }
  };

  const pct = user?.profileComplete || 30;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* Hero banner */}
      <div className="profile-hero">
        <div>
          <div className="profile-hero-av" style={{ background: `#${user?.avatarBg || '1DB954'}`, overflow: 'hidden', position: 'relative' }}>
            {user?.photoUrl
              ? <img src={user.photoUrl} alt={user?.name} style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
              : (user?.initials || user?.name?.charAt(0) || 'M')
            }
          </div>
        </div>
        <div className="profile-hero-info">
          <h1 className="profile-hero-title">Edit Your Profile</h1>
          <p className="profile-hero-sub">A complete profile gets you discovered and booked faster.</p>
          <div className="profile-hero-progress">
            <div className="profile-hero-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="profile-hero-pct">{pct}% complete</div>
          <div className="profile-step-chips">
            <span className="profile-step-chip done">Basic Info</span>
            <span className="profile-step-chip done">Professional</span>
            <span className="profile-step-chip done">Pricing</span>
            <span className="profile-step-chip todo">Social Links</span>
          </div>
        </div>
      </div>

      {/* Section 0 — Profile Photo */}
      <ProfilePhotoUpload user={user} updateUser={updateUser} accentColor="#16a34a" />

      {/* Section 1 — Basic Information */}
      <div className="db-card profile-section-basic">
        <div className="db-section-title" style={{ marginBottom: 14 }}>👤 Basic Information</div>
        <div className="db-grid-2" style={{ gap: 12 }}>
          <div>
            <label className="db-field-label">Full Name</label>
            <input className="db-field" value={f.name} onChange={e => setF(x => ({ ...x, name: e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Headline</label>
            <input className="db-field" value={f.headline} placeholder="e.g. Senior Engineer at Google · Ex-Amazon" onChange={e => setF(x => ({ ...x, headline: e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Location</label>
            <input className="db-field" value={f.location} onChange={e => setF(x => ({ ...x, location: e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Timezone</label>
            <select className="db-field" value={f.timezone} onChange={e => setF(x => ({ ...x, timezone: e.target.value }))}>
              <option value="">Select timezone</option>
              <option value="IST">IST — India Standard Time</option>
              <option value="SGT">SGT — Singapore Time</option>
              <option value="GST">GST — Gulf Standard Time</option>
              <option value="GMT">GMT — Greenwich Mean Time</option>
              <option value="EST">EST — Eastern Standard Time</option>
              <option value="PST">PST — Pacific Standard Time</option>
            </select>
          </div>
          <div>
            <label className="db-field-label">Phone</label>
            <input className="db-field" value={f.phone} placeholder="+91 9876543210" onChange={e => setF(x => ({ ...x, phone: e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Languages Spoken</label>
            <input className="db-field" value={f.languages} placeholder="English, Tamil, Hindi" onChange={e => setF(x => ({ ...x, languages: e.target.value }))} />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label className="db-field-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
            Bio
            <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: '.75rem' }}>{f.bio.length}/600</span>
          </label>
          <textarea
            className="db-field db-textarea"
            value={f.bio}
            maxLength={600}
            rows={5}
            placeholder="Tell mentees about your background, expertise, and what makes your sessions valuable..."
            onChange={e => setF(x => ({ ...x, bio: e.target.value }))}
          />
        </div>
      </div>

      {/* Section 2 — Professional Details */}
      <div className="db-card profile-section-pro">
        <div className="db-section-title" style={{ marginBottom: 14 }}>💼 Professional Details</div>
        <div className="db-grid-2" style={{ gap: 12 }}>
          <div>
            <label className="db-field-label">Current Company</label>
            <input className="db-field" value={f.company} onChange={e => setF(x => ({ ...x, company: e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Current Role / Title</label>
            <input className="db-field" value={f.role} onChange={e => setF(x => ({ ...x, role: e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Total Years of Experience</label>
            <select className="db-field" value={f.experience} onChange={e => setF(x => ({ ...x, experience: e.target.value }))}>
              <option value="">Select</option>
              {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
                <option key={n} value={String(n)}>{n} {n === 1 ? 'year' : 'years'}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="db-field-label">Skills (comma-separated)</label>
            <input className="db-field" value={f.skills} placeholder="React, Node.js, AWS, TypeScript" onChange={e => setF(x => ({ ...x, skills: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* Section 3 — Work Experience */}
      <div className="db-card profile-section-pro">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span className="db-section-title" style={{ marginBottom: 0 }}>🏢 Work Experience</span>
          <button className="btn btn-outline btn-sm" onClick={addWorkExp}>+ Add</button>
        </div>
        {workExp.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: '#9ca3af' }}>
            <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>🏢</div>
            <div style={{ fontSize: '.84rem', marginBottom: 12 }}>No work experience added yet.</div>
            <button className="btn btn-outline btn-sm" onClick={addWorkExp}>+ Add Work Experience</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {workExp.map((job, i) => (
              <div key={job.id || i} style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb', position: 'relative' }}>
                <button
                  onClick={() => setWorkExp(p => p.filter((_, idx) => idx !== i))}
                  style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#9ca3af', lineHeight: 1 }}
                >✕</button>
                <div style={{ fontSize: '.68rem', fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
                  EXPERIENCE {i + 1}
                </div>
                <div className="db-grid-2" style={{ gap: 10 }}>
                  <div>
                    <label className="db-field-label">Company</label>
                    <input className="db-field" value={job.company} onChange={e => setWorkExp(p => p.map((x, idx) => idx === i ? { ...x, company: e.target.value } : x))} />
                  </div>
                  <div>
                    <label className="db-field-label">Role</label>
                    <input className="db-field" value={job.role} onChange={e => setWorkExp(p => p.map((x, idx) => idx === i ? { ...x, role: e.target.value } : x))} />
                  </div>
                  <div>
                    <label className="db-field-label">Start Year</label>
                    <input className="db-field" value={job.startYear} placeholder="2020" onChange={e => setWorkExp(p => p.map((x, idx) => idx === i ? { ...x, startYear: e.target.value } : x))} />
                  </div>
                  <div>
                    <label className="db-field-label" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      End Year
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 400, fontSize: '.75rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!job.isCurrent}
                          onChange={e => setWorkExp(p => p.map((x, idx) => idx === i ? { ...x, isCurrent: e.target.checked } : x))}
                        /> Current
                      </label>
                    </label>
                    <input
                      className="db-field"
                      value={job.endYear}
                      placeholder="2023"
                      disabled={!!job.isCurrent}
                      style={{ opacity: job.isCurrent ? 0.4 : 1 }}
                      onChange={e => setWorkExp(p => p.map((x, idx) => idx === i ? { ...x, endYear: e.target.value } : x))}
                    />
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <label className="db-field-label">Description <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                  <textarea
                    className="db-field db-textarea"
                    rows={2}
                    value={job.description}
                    placeholder="Key achievements, technologies used..."
                    onChange={e => setWorkExp(p => p.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section 4 — Education */}
      <div className="db-card profile-section-pro">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span className="db-section-title" style={{ marginBottom: 0 }}>🎓 Education</span>
          <button className="btn btn-outline btn-sm" onClick={addEduc}>+ Add</button>
        </div>
        {educ.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: '#9ca3af' }}>
            <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>🎓</div>
            <div style={{ fontSize: '.84rem', marginBottom: 12 }}>No education added yet.</div>
            <button className="btn btn-outline btn-sm" onClick={addEduc}>+ Add Education</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {educ.map((edu, i) => (
              <div key={edu.id || i} style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb', position: 'relative' }}>
                <button
                  onClick={() => setEduc(p => p.filter((_, idx) => idx !== i))}
                  style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#9ca3af', lineHeight: 1 }}
                >✕</button>
                <div className="db-grid-2" style={{ gap: 10 }}>
                  <div>
                    <label className="db-field-label">Institution</label>
                    <input className="db-field" value={edu.institution} onChange={e => setEduc(p => p.map((x, idx) => idx === i ? { ...x, institution: e.target.value } : x))} />
                  </div>
                  <div>
                    <label className="db-field-label">Degree</label>
                    <input className="db-field" value={edu.degree} placeholder="B.Tech Computer Science" onChange={e => setEduc(p => p.map((x, idx) => idx === i ? { ...x, degree: e.target.value } : x))} />
                  </div>
                  <div>
                    <label className="db-field-label">Year</label>
                    <input className="db-field" value={edu.year} placeholder="2018" onChange={e => setEduc(p => p.map((x, idx) => idx === i ? { ...x, year: e.target.value } : x))} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section 5 — Certifications & Achievements */}
      <div className="db-card profile-section-basic">
        <div className="db-section-title" style={{ marginBottom: 6 }}>🏆 Certifications & Achievements</div>
        <p style={{ fontSize: '.78rem', color: '#6b7280', marginBottom: 10 }}>One per line — e.g. AWS Solutions Architect – 2023</p>
        <textarea
          className="db-field db-textarea"
          rows={4}
          value={certs.join('\n')}
          placeholder={"AWS Solutions Architect – 2023\nGoogle Cloud Professional – 2022"}
          onChange={e => setCerts(e.target.value.split('\n'))}
        />
      </div>

      {/* Section 6 — Session Pricing */}
      <div className="db-card profile-section-price">
        <div className="db-section-title" style={{ marginBottom: 14 }}>💰 Session Pricing</div>
        <div className="db-grid-2" style={{ gap: 12 }}>
          <div>
            <label className="db-field-label">Price / hr (INR)</label>
            <input className="db-field" type="number" value={f.price} onChange={e => setF(x => ({ ...x, price: e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Session Duration</label>
            <select className="db-field">
              <option>60 minutes</option>
              <option>45 minutes</option>
              <option>30 minutes</option>
              <option>90 minutes</option>
            </select>
          </div>
        </div>
      </div>

      {/* Earnings callout */}
      <div className="profile-earn-callout">
        <div className="profile-earn-callout-ico">⚡</div>
        <div className="profile-earn-callout-text">
          <h4>Set your session price and start earning within 30 minutes!</h4>
          <p>Mentors who complete their profile get their first booking within a day on average.</p>
        </div>
      </div>

      {/* Section 7 — Social & Links */}
      <div className="db-card profile-section-social">
        <div className="db-section-title" style={{ marginBottom: 14 }}>🔗 Social & Links</div>
        <div className="db-grid-2" style={{ gap: 12 }}>
          <div>
            <label className="db-field-label">LinkedIn</label>
            <input className="db-field" value={f.linkedin} placeholder="https://linkedin.com/in/yourprofile" onChange={e => setF(x => ({ ...x, linkedin: e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">GitHub</label>
            <input className="db-field" value={f.github} placeholder="https://github.com/yourusername" onChange={e => setF(x => ({ ...x, github: e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Twitter / X</label>
            <input className="db-field" value={f.twitter} placeholder="https://twitter.com/yourhandle" onChange={e => setF(x => ({ ...x, twitter: e.target.value }))} />
          </div>
          <div>
            <label className="db-field-label">Portfolio</label>
            <input className="db-field" value={f.portfolio} placeholder="https://yourportfolio.com" onChange={e => setF(x => ({ ...x, portfolio: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* Save action row */}
      <div className="profile-save-row">
        <button className="btn btn-primary" style={{ minWidth: 180 }} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : saved ? 'Saved!' : 'Save Profile'}
        </button>
        <a href={`/mentors/${slug}`} target="_blank" rel="noreferrer" className="btn btn-outline">
          Preview Public Profile
        </a>
      </div>
    </div>
  );
}

function Reviews({ reviews = [] }) {
  const myReviews = reviews[1] || [];
  const avg = (myReviews.reduce((s,r)=>s+r.rating,0)/myReviews.length).toFixed(1);
  return (
    <div>
      <h1 className="db-page-title">Reviews & Ratings</h1><p className="db-page-sub">What your mentees say about you.</p>
      <div className="db-grid-2" style={{marginBottom:24}}>
        <div className="db-card" style={{textAlign:'center',padding:32}}><div style={{fontSize:'3.2rem',fontWeight:800,color:'#1a1a2e'}}>{avg}</div><div style={{fontSize:'1.3rem',color:'#f59e0b',marginBottom:4}}>{'★'.repeat(Math.round(avg))}</div><div style={{fontSize:'.84rem',color:'#6b7280'}}>{myReviews.length} verified reviews</div></div>
        <div className="db-card">{[5,4,3,2,1].map(n=>{const count=myReviews.filter(r=>r.rating===n).length;const pct=(count/myReviews.length)*100;return(<div key={n} style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}><span style={{fontSize:'.75rem',fontWeight:700,color:'#6b7280',width:28,textAlign:'right'}}>{n} ★</span><div style={{flex:1,height:8,background:'#f3f4f6',borderRadius:100,overflow:'hidden'}}><div style={{width:`${pct}%`,height:'100%',background:'#f59e0b',borderRadius:100}}/></div><span style={{fontSize:'.72rem',color:'#9ca3af',width:20}}>{count}</span></div>);})}</div>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:14}}>
        {myReviews.map(r=>(
          <div key={r.id} className="db-card">
            <div style={{display:'flex',gap:12,alignItems:'flex-start',marginBottom:10}}>
              <Av seed={r.avatarSeed} bg={r.avatarBg} init={r.initials} size={42}/>
              <div style={{flex:1,display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8}}>
                <div><div style={{fontWeight:800,fontSize:'.875rem',color:'#1a1a2e'}}>{r.name}</div><div style={{fontSize:'.72rem',color:'#9ca3af'}}>{r.sessionType} · {r.date}</div></div>
                <div style={{color:'#f59e0b',fontSize:'.88rem'}}>{'★'.repeat(r.rating)}</div>
              </div>
            </div>
            <p style={{fontSize:'.875rem',color:'#374151',lineHeight:1.7,marginBottom:12}}>{r.comment}</p>
            <div style={{paddingTop:12,borderTop:'1px solid #f3f4f6',display:'flex',gap:10}}>
              <input className="db-field" placeholder="Write a reply..." style={{flex:1,padding:'8px 12px'}}/>
              <button className="btn btn-primary btn-sm">Reply</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── MY EVENTS ─────────────────────────────────────────────── */
function MyEvents() {
  const { user: authUser }               = useAuth();
  const { currency }                     = useContext(AppContext);
  const { getEnabledMeetingPlatforms }   = useIntegrations();
  const enabledMeetingPlatforms          = getEnabledMeetingPlatforms();
  const [events, setEvents]              = useState([]);
  const [evLoading, setEvLoading]        = useState(true);
  const [showForm, setShowForm]          = useState(false);
  const [editId, setEditId]             = useState(null);
  const [form, setForm]                  = useState(EMPTY_EVENT);
  const [tab, setTab]                    = useState('upcoming'); // upcoming | completed
  const [saving, setSaving]              = useState(false);

  // In mock mode Amar Krishnan = mentorId 1
  const isMock    = process.env.REACT_APP_MOCK !== 'false';
  const mentorId  = isMock ? 1 : authUser?.mentorId;

  // Load mentor's events from the shared service store
  const loadEvents = () =>
    eventService.getMyEvents(mentorId)
      .then(res => setEvents(res.events || []))
      .catch(err => console.error('My Events load error:', err))
      .finally(() => setEvLoading(false));

  useEffect(() => { loadEvents(); }, []); // eslint-disable-line

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openCreate = () => { setForm(EMPTY_EVENT); setEditId(null); setShowForm(true); };
  const openEdit   = (ev) => {
    setForm({
      title:           ev.title,
      type:            ev.type || 'workshop',
      description:     ev.description || '',
      date:            ev.date,
      time:            ev.time || '',
      // duration may be a number (minutes) — convert to string for the select
      duration:        String(typeof ev.duration === 'number' ? ev.duration : parseInt(ev.duration) || 60),
      priceType:       ev.priceINR === 0 ? 'free' : 'paid',
      priceINR:        ev.priceINR === 0 ? '' : String(ev.priceINR),
      maxAttendees:    String(ev.maxAttendees || 100),
      meetingPlatform: ev.meetingPlatform || 'google_meet',
      meetingLink:     ev.meetingLink || '',
      linkedin:        ev.socialLinks?.linkedin || '',
      twitter:         ev.socialLinks?.twitter  || '',
    });
    setEditId(ev.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.date) return;
    setSaving(true);
    const payload = {
      title:           form.title,
      type:            form.type,
      description:     form.description,
      date:            form.date,
      time:            form.time,
      duration:        Number(form.duration) || 60,
      priceINR:        form.priceType === 'free' ? 0 : (Number(form.priceINR) || 0),
      maxAttendees:    Number(form.maxAttendees) || 100,
      meetingPlatform: form.meetingPlatform,
      meetingLink:     form.meetingLink,
      linkedin:        form.linkedin,
      twitter:         form.twitter,
      // Mentor identity — real mode uses authUser fields
      mentor:          authUser?.name       || 'Amar Krishnan',
      mentorId:        mentorId,
      mentorAvatar:    authUser?.firstName  || 'Amar',
      mentorBg:        authUser?.avatarBg   || '16a34a',
      mentorRole:      authUser?.headline   || 'Senior Mentor',
      tags:            [],
    };
    try {
      if (editId) {
        await eventService.updateEvent(editId, payload);
      } else {
        await eventService.createEvent(payload);
      }
      await loadEvents();
      setShowForm(false);
    } catch (err) {
      console.error('Save event error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Cancel and remove this event?')) return;
    try {
      await eventService.deleteEvent(id);
      await loadEvents();
    } catch (err) {
      console.error('Delete event error:', err);
    }
  };

  const shown = events.filter(e =>
    tab === 'upcoming' ? e.status !== 'completed' : e.status === 'completed'
  );

  const statusColor = { upcoming:'#2563eb', live:'#16a34a', completed:'#6b7280', cancelled:'#dc2626' };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 className="db-page-title">🎤 My Events</h1>
          <p className="db-page-sub">Create and manage your workshops, webinars, and office hours.</p>
        </div>
        <button className="btn btn-primary" style={{ padding:'9px 20px', fontSize:'.86rem' }} onClick={openCreate}>
          + Create Event
        </button>
      </div>

      {/* Stats row */}
      <div className="db-stats-grid-4" style={{ marginBottom:24 }}>
        {[
          { icon:'📅', label:'Total Events',    v: events.length },
          { icon:'👥', label:'Total Registrations', v: events.reduce((s,e)=>s+(e.registeredCount||0),0) },
          { icon:'⭐', label:'Avg Rating',      v: events.filter(e=>e.rating).length ? (events.filter(e=>e.rating).reduce((s,e)=>s+e.rating,0)/events.filter(e=>e.rating).length).toFixed(1) : '—' },
          { icon:'💰', label:'Events Revenue',  v: convertPrice(events.filter(e=>e.priceINR>0).reduce((s,e)=>s+(e.priceINR*(e.registeredCount||0)),0), currency) },
        ].map(s => (
          <div key={s.label} className="db-stat-card">
            <div className="dsc-icon">{s.icon}</div>
            <div className="dsc-label">{s.label}</div>
            <div className="dsc-value">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <div className="db-card" style={{ marginBottom:24, border:'2px solid #bbf7d0' }}>
          <div style={{ padding:'14px 20px', borderBottom:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontWeight:800, fontSize:'.95rem' }}>{editId ? '✏️ Edit Event' : '➕ Create New Event'}</span>
            <button onClick={() => setShowForm(false)} style={{ background:'none', border:'none', fontSize:'1.2rem', cursor:'pointer', color:'#9ca3af' }}>✕</button>
          </div>
          <div style={{ padding:'20px' }}>

            {/* Row 1: Title + Type */}
            <div className="db-form-grid" style={{ marginBottom:0 }}>
              <div className="db-form-field" style={{ gridColumn:'1/-1' }}>
                <label className="db-form-label">Event Title *</label>
                <input className="db-form-input" placeholder="e.g. React Performance Masterclass" value={form.title} onChange={e=>set('title',e.target.value)} />
              </div>
              <div className="db-form-field">
                <label className="db-form-label">Event Type</label>
                <select className="db-form-input" value={form.type} onChange={e=>set('type',e.target.value)}>
                  {EVENT_TYPES.map(t => <option key={t} value={t}>{t.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
                </select>
              </div>
              <div className="db-form-field">
                <label className="db-form-label">Duration (minutes)</label>
                <select className="db-form-input" value={form.duration} onChange={e=>set('duration',e.target.value)}>
                  {['30','45','60','90','120','180'].map(d=><option key={d}>{d}</option>)}
                </select>
              </div>
            </div>

            {/* Description */}
            <div className="db-form-field" style={{ marginTop:14 }}>
              <label className="db-form-label">Description *</label>
              <textarea className="db-form-input" rows={3} style={{ resize:'vertical' }} placeholder="What will attendees learn? What should they bring?" value={form.description} onChange={e=>set('description',e.target.value)} />
            </div>

            {/* Row 2: Date + Time */}
            <div className="db-form-grid" style={{ marginTop:14 }}>
              <div className="db-form-field">
                <label className="db-form-label">Date *</label>
                <input className="db-form-input" type="date" value={form.date} onChange={e=>set('date',e.target.value)} />
              </div>
              <div className="db-form-field">
                <label className="db-form-label">Time</label>
                <input className="db-form-input" type="time" value={form.time} onChange={e=>set('time',e.target.value)} />
              </div>
              <div className="db-form-field">
                <label className="db-form-label">Max Attendees</label>
                <input className="db-form-input" type="number" min={1} placeholder="100" value={form.maxAttendees} onChange={e=>set('maxAttendees',e.target.value)} />
              </div>
            </div>

            {/* ── Pricing — Free or Paid ── */}
            <div style={{ marginTop:18 }}>
              <label className="db-form-label" style={{ marginBottom:10, display:'block' }}>Pricing</label>
              <div style={{ display:'flex', gap:12, marginBottom:14 }}>
                {[['free','🆓 Free','Open to everyone at no cost'],['paid','💰 Paid','Charge attendees a fee']].map(([val,lbl,hint])=>(
                  <button key={val} onClick={()=>set('priceType',val)}
                    style={{ flex:1, padding:'14px 16px', borderRadius:12, border: form.priceType===val ? '2px solid #16a34a' : '1.5px solid #e5e7eb',
                      background: form.priceType===val ? '#f0fdf4' : '#fff', cursor:'pointer', textAlign:'left', fontFamily:'inherit', transition:'all .15s' }}>
                    <div style={{ fontWeight:800, fontSize:'.9rem', marginBottom:3 }}>{lbl}</div>
                    <div style={{ fontSize:'.75rem', color:'#6b7280' }}>{hint}</div>
                  </button>
                ))}
              </div>
              {form.priceType === 'paid' && (
                <div className="db-form-field" style={{ maxWidth:240 }}>
                  <label className="db-form-label">Price (₹) *</label>
                  <input className="db-form-input" type="number" min={1} placeholder="499" value={form.priceINR} onChange={e=>set('priceINR',e.target.value)} />
                </div>
              )}
            </div>

            {/* ── Meeting Platform ── */}
            <div style={{ marginTop:18 }}>
              <label className="db-form-label" style={{ marginBottom:10, display:'block' }}>Meeting Platform</label>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:12 }}>
                {enabledMeetingPlatforms.map(p=>(
                  <button key={p.id} onClick={()=>set('meetingPlatform',p.id)}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 16px', borderRadius:10,
                      border: form.meetingPlatform===p.id ? '2px solid #16a34a' : '1.5px solid #e5e7eb',
                      background: form.meetingPlatform===p.id ? '#f0fdf4' : '#fff', cursor:'pointer', fontFamily:'inherit',
                      fontWeight:700, fontSize:'.82rem', transition:'all .15s' }}>
                    <span>{p.icon}</span> {p.label}
                  </button>
                ))}
              </div>
              <div className="db-form-field">
                <label className="db-form-label">Meeting Link (optional — can add after)</label>
                <input className="db-form-input" placeholder={enabledMeetingPlatforms.find(p=>p.id===form.meetingPlatform)?.id === 'zoom' ? 'https://zoom.us/j/...' : 'https://meet.google.com/...'} value={form.meetingLink} onChange={e=>set('meetingLink',e.target.value)} />
              </div>
            </div>

            {/* ── Social Share Links ── */}
            <div className="db-form-grid" style={{ marginTop:14 }}>
              <div className="db-form-field">
                <label className="db-form-label">🔗 LinkedIn Post URL (optional)</label>
                <input className="db-form-input" placeholder="https://linkedin.com/posts/..." value={form.linkedin} onChange={e=>set('linkedin',e.target.value)} />
              </div>
              <div className="db-form-field">
                <label className="db-form-label">🐦 Twitter/X Post URL (optional)</label>
                <input className="db-form-input" placeholder="https://twitter.com/..." value={form.twitter} onChange={e=>set('twitter',e.target.value)} />
              </div>
            </div>

            {/* Actions */}
            <div style={{ display:'flex', gap:10, marginTop:20, paddingTop:16, borderTop:'1px solid #f3f4f6' }}>
              <button className="btn btn-primary" style={{ padding:'10px 24px' }} onClick={handleSave}
                disabled={saving || !form.title.trim() || !form.date || (form.priceType==='paid' && !form.priceINR)}>
                {saving ? '⏳ Saving…' : editId ? '💾 Save Changes' : '🚀 Publish Event'}
              </button>
              <button className="btn btn-outline" style={{ padding:'10px 18px' }} onClick={() => setShowForm(false)} disabled={saving}>Cancel</button>
              {form.priceType==='paid' && !form.priceINR && (
                <span style={{ fontSize:'.75rem', color:'#ef4444', alignSelf:'center' }}>⚠ Enter a price for paid event</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab switcher */}
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {['upcoming','completed'].map(t=>(
          <button key={t} className={`btn btn-sm ${tab===t?'btn-primary':'btn-outline'}`} onClick={()=>setTab(t)}>
            {t==='upcoming' ? `📅 Upcoming / Live (${events.filter(e=>e.status!=='completed').length})` : `✅ Completed (${events.filter(e=>e.status==='completed').length})`}
          </button>
        ))}
      </div>

      {/* Events list */}
      {evLoading ? (
        <DashboardSkeleton cols={1} />
      ) : shown.length === 0 ? (
        <div className="db-empty">
          <div className="db-empty-ico">🎤</div>
          <h3>{tab==='upcoming' ? 'No upcoming events' : 'No completed events yet'}</h3>
          <p>Create your first event to engage your audience and grow your following.</p>
          <button className="btn btn-primary" style={{ marginTop:14 }} onClick={openCreate}>+ Create Your First Event</button>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {shown.map(ev => (
            <div key={ev.id} className="db-card" style={{ padding:'18px 20px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:6 }}>
                    <span style={{ fontWeight:800, fontSize:'.95rem' }}>{ev.title}</span>
                    <span style={{ background:'#f3f4f6', padding:'2px 9px', borderRadius:6, fontSize:'.68rem', fontWeight:700, color:'#374151' }}>
                      {(ev.type||'').replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}
                    </span>
                    <span style={{ background: ev.priceINR===0?'#dcfce7':'#fef3c7', color: ev.priceINR===0?'#166534':'#92400e', padding:'2px 9px', borderRadius:6, fontSize:'.68rem', fontWeight:800 }}>
                      {ev.priceINR===0 ? '🆓 Free' : `💰 ₹${ev.priceINR}`}
                    </span>
                    <span style={{ color: statusColor[ev.status]||'#6b7280', fontSize:'.72rem', fontWeight:700 }}>● {ev.status}</span>
                  </div>
                  <div style={{ fontSize:'.8rem', color:'#6b7280', display:'flex', gap:16, flexWrap:'wrap' }}>
                    <span>📅 {ev.date}{ev.time ? ` at ${ev.time}` : ''}</span>
                    <span>👥 {ev.registeredCount} / {ev.maxAttendees} registered</span>
                    {ev.duration && <span>⏱ {ev.duration} min</span>}
                    {ev.rating && <span>⭐ {ev.rating} ({ev.totalRatings} ratings)</span>}
                  </div>
                  {/* Registration progress bar */}
                  <div style={{ marginTop:10, maxWidth:300 }}>
                    <div style={{ height:5, background:'#f3f4f6', borderRadius:99, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${Math.min(100,(ev.registeredCount/ev.maxAttendees)*100)}%`,
                        background: ev.registeredCount>=ev.maxAttendees ? '#ef4444' : '#16a34a', borderRadius:99, transition:'width .4s' }} />
                    </div>
                    <div style={{ fontSize:'.68rem', color:'#9ca3af', marginTop:3 }}>
                      {ev.registeredCount >= ev.maxAttendees ? '🔴 Full' : `${ev.maxAttendees - ev.registeredCount} spots left`}
                    </div>
                  </div>
                </div>
                {ev.status !== 'completed' && (
                  <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => openEdit(ev)}>✏️ Edit</button>
                    <button className="btn btn-sm" style={{ background:'#fef2f2', color:'#ef4444', border:'1px solid #fecaca', borderRadius:8 }} onClick={() => handleDelete(ev.id)}>🗑 Cancel</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MentorDashboard() {
  const { user: authUser } = useAuth();
  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const [showTour, setShowTour] = useState(false);

  // Show onboarding tour once for newly approved mentors
  useEffect(() => {
    if (!authUser?.isApproved || authUser?.role !== 'mentor') return;
    if (!hasMentorSeenTour(authUser.id)) setShowTour(true);
  }, [authUser?.id]); // eslint-disable-line

  const [upcomingSessions, setUpcomingSessions] = useState(isMock ? (MENTOR_DASHBOARD.upcomingSessions || []) : []);
  const [walletData,       setWalletData]       = useState(isMock ? { balance: MENTOR_DASHBOARD.wallet?.balance || 0, totalEarned: 0 } : { balance: 0, totalEarned: 0 });
  const [reviews]                                = useState(isMock ? (MENTOR_REVIEWS || []) : []);
  const [pastSessions,     setPastSessions]      = useState(isMock ? (MENTOR_DASHBOARD.pastSessions || []) : []);
  const [dashLoading,      setDashLoading]       = useState(!isMock);

  useEffect(() => {
    if (isMock) return;
    if (!sessionStorage.getItem('db_user')) { setDashLoading(false); return; }

    // Return cached data immediately, then refresh in background
    const cached = getCached('mentor_dashboard');
    if (cached) {
      if (cached.sessions?.length)  setUpcomingSessions(cached.sessions);
      if (cached.past?.length)      setPastSessions(cached.past);
      if (cached.wallet)            setWalletData(cached.wallet);
      setDashLoading(false);
    }

    const normalizeSession = b => ({
      ...b,
      id:       b._id || b.id,
      mentee:   b.menteeId ? `${b.menteeId.firstName} ${b.menteeId.lastName}` : 'Mentee',
      menteeInit: b.menteeId ? `${b.menteeId.firstName?.[0]||''}${b.menteeId.lastName?.[0]||''}` : 'M',
      menteeBg: b.menteeId?.avatarBg || '1DB954',
      type:     b.sessionType || '1:1 Session',
      date:     b.sessionDate ? new Date(b.sessionDate).toLocaleDateString('en-IN', { day:'numeric', month:'short' }) : '',
      time:     b.sessionTime || '',
      link:     b.meetingLink || '#',
      platform: b.meetingPlatform || 'google_meet',
      status:   b.status || 'confirmed',
      priceINR: b.priceINR || 0,
    });

    Promise.all([
      bookingService.getUpcomingSessions(),
      bookingService.getPastSessions(),
      walletService.getWallet(),
    ]).then(([upRes, pastRes, walletRes]) => {
      const normedUp   = (upRes.sessions||[]).map(normalizeSession);
      const normedPast = (pastRes.sessions||[]).map(normalizeSession);
      const wallet     = { balance: walletRes.balance||0, totalEarned: walletRes.totalEarned||0 };
      if (normedUp.length)   setUpcomingSessions(normedUp);
      if (normedPast.length) setPastSessions(normedPast);
      setWalletData(wallet);
      setCached('mentor_dashboard', { sessions:normedUp, past:normedPast, wallet });
    })
    .catch(err => console.error('Mentor dashboard load error:', err))
    .finally(() => setDashLoading(false));
  }, []); // eslint-disable-line

  const { pathname } = useLocation();
  const sectionMap = {
    '/dashboard/mentor':'Overview',
    '/dashboard/mentor/sessions':'Sessions',
    '/dashboard/mentor/availability':'Availability',
    '/dashboard/mentor/earnings':'Earnings',
    '/dashboard/mentor/events':'My Events',
    '/dashboard/mentor/services':'My Services',
    '/dashboard/mentor/profile':'Profile',
    '/dashboard/mentor/reviews':'Reviews',
    '/dashboard/mentor/security':'Security',
  };
  const section = Object.entries(sectionMap).find(([k])=>k===pathname)?.[1]||'Overview';
  if (dashLoading) {
    return (
      <DashboardLayout activeSection={section}>
        <DashboardSkeleton cols={4} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeSection={section}>
      {!authUser?.isApproved && authUser?.role === 'mentor' && (
        <div style={{ padding: '16px 0 0' }}>
          <MentorApplicationStatus
            userId={authUser?.id}
            onReapply={() => {
              // Mock: reset application to pending on reapply
              // TODO §BACKEND: POST /api/mentor-applications/reapply { userId }
              if (authUser?.id) {
                setMentorAppStatus(authUser.id, { status: 'pending', submittedAt: Date.now() });
                // Force re-render by triggering a state update
                window.location.reload();
              }
            }}
          />
        </div>
      )}
      <Routes>
        <Route index element={<Overview upcoming={upcomingSessions} wallet={walletData} stats={null} user={authUser}/>}/>
        <Route path="sessions" element={<Sessions upcoming={upcomingSessions} past={pastSessions}/>}/>
        <Route path="availability" element={<Availability/>}/>
        <Route path="earnings" element={<Earnings wallet={walletData}/>}/>
        <Route path="events" element={<MyEvents/>}/>
        <Route path="services" element={<SessionCatalog/>}/>
        <Route path="profile" element={<Profile/>}/>
        <Route path="reviews" element={<Reviews reviews={reviews}/>}/>
        <Route path="security"      element={<SecurityPage/>}/>
        <Route path="share"         element={<MentorSharePage/>}/>
      </Routes>
      <MentorOnboardingTour
        open={showTour}
        onClose={() => {
          markMentorTourSeen(authUser?.id);
          setShowTour(false);
        }}
        userName={authUser?.firstName || 'Mentor'}
      />
    </DashboardLayout>
  );
}
